/**
 * Prompt 22 item 2b — repair↔device match review. No silent attachment,
 * ever: before the job is created, each quoted line renders next to the
 * device it matched with evidence ("serial match" / "serial + model
 * match"). Ambiguous lines get a device picker (or an explicit skip);
 * the estimate cannot convert until every line is resolved.
 *
 * #1611: a line naming a device the site doesn't have ("unmatched") gets the
 * same picker but does NOT hold the conversion — left unpicked, the job is
 * created without it and the office is told "N lines didn't match a device —
 * pick or create". Declined devices show as such and never link.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { AlertTriangle, CheckCircle, FileText, X } from "lucide-react";
import { conversionToast, type ConversionScope } from "@/lib/estimate-conversion";

interface PreviewDevice {
    id: string;
    name: string | null;
    serial_number: string | null;
    manufacturer: string | null;
    model: string | null;
    size_label: string | null;
    location_description: string | null;
}

interface PreviewLine {
    lineId: string;
    sortOrder: number;
    description: string;
    serials: string[];
    status: "matched" | "ambiguous" | "unmatched" | "declined" | "no_serial";
    matchedAssetId: string | null;
    matchedAsset: PreviewDevice | null;
    evidence: string | null;
    reviewReason: string | null;
    candidates: PreviewDevice[];
}

interface PreviewResponse {
    estimate: {
        id: string;
        display_number: string | null;
        title: string | null;
        site_name: string | null;
        company_name: string | null;
    };
    status: "clean" | "needs_review" | "no_serials";
    lines: PreviewLine[];
    devices: Array<{ asset: PreviewDevice; evidence: string; bypass: PreviewDevice | null }>;
    wo_match: { itemId: string; woNumber: string | null; reason: string } | null;
    unmatched_line_count?: number;
}

function deviceLabel(d: PreviewDevice): string {
    const head = [d.size_label, d.manufacturer, d.model].filter(Boolean).join(" ") || d.name || "Device";
    const parts = [head];
    if (d.serial_number) parts.push(`S/N ${d.serial_number}`);
    if (d.location_description) parts.push(d.location_description);
    return parts.join(" · ");
}

export function ConversionReviewDialog({
    estimateId,
    onClose,
    onConverted,
}: {
    estimateId: string;
    onClose: () => void;
    onConverted?: (jobId: string) => void;
}) {
    const qc = useQueryClient();
    const navigate = useNavigate();
    // lineId -> assetId ("" = skip the line)
    const [picks, setPicks] = useState<Record<string, string>>({});

    const previewQ = useQuery({
        queryKey: ["estimate-conversion-preview", estimateId],
        queryFn: () => api.get<PreviewResponse>(`/api/estimates/${estimateId}/conversion-preview`),
    });
    const preview = previewQ.data;

    // Pre-select the first candidate for single-candidate ambiguous lines so
    // the office confirms rather than hunts.
    useEffect(() => {
        if (!preview) return;
        setPicks((prev) => {
            const next = { ...prev };
            for (const l of preview.lines) {
                if (l.status === "ambiguous" && next[l.lineId] === undefined && l.candidates.length === 1) {
                    next[l.lineId] = l.candidates[0].id;
                }
            }
            return next;
        });
    }, [preview]);

    const ambiguous = (preview?.lines ?? []).filter((l) => l.status === "ambiguous");
    const unresolved = ambiguous.filter((l) => picks[l.lineId] === undefined);
    // Unmatched lines are optional picks — only the ones the office picked go up.
    const unmatched = (preview?.lines ?? []).filter((l) => l.status === "unmatched");
    const unmatchedLeft = unmatched.filter((l) => !picks[l.lineId]).length;

    const convertM = useMutation({
        mutationFn: () =>
            api.post<ConversionScope & { job: { id: string } }>(`/api/estimates/${estimateId}/convert-to-job`, {
                overrides: [
                    ...ambiguous.map((l) => ({
                        lineId: l.lineId,
                        assetId: picks[l.lineId] === "" ? null : (picks[l.lineId] ?? null),
                    })),
                    ...unmatched
                        .filter((l) => picks[l.lineId])
                        .map((l) => ({ lineId: l.lineId, assetId: picks[l.lineId] })),
                ],
            }),
        onSuccess: (data) => {
            qc.invalidateQueries({ queryKey: ["commercial"] });
            const t = conversionToast(data);
            if (t.warn) toast.warning(t.text, { duration: 12000 });
            else toast.success(t.text);
            onConverted?.(data.job.id);
            onClose();
            navigate(`/jobs/${data.job.id}`);
        },
        onError: (e) => {
            const body = e instanceof ApiError ? (e.body as { message?: string }) : undefined;
            toast.error(body?.message ?? "Couldn't create the job");
        },
    });

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <div
                className="w-full max-w-2xl rounded-lg border bg-card shadow-xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b px-4 py-3">
                    <h3 className="text-sm font-semibold">
                        Match review — estimate {preview?.estimate.display_number ?? ""}
                    </h3>
                    <button type="button" onClick={onClose} className="text-foreground/80 hover:text-foreground">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="space-y-3 p-4">
                    {previewQ.isLoading && (
                        <div className="flex justify-center py-8">
                            <Spinner />
                        </div>
                    )}
                    {previewQ.isError && (
                        <p className="text-sm text-destructive">Couldn't load the match preview — try again.</p>
                    )}

                    {preview && (
                        <>
                            {preview.wo_match && (
                                <div className="flex items-start gap-2 rounded-lg border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
                                    <FileText className="mt-0.5 h-4 w-4 shrink-0" />
                                    <p>
                                        <span className="font-medium">
                                            Work order {preview.wo_match.woNumber ?? ""} will attach automatically.
                                        </span>{" "}
                                        {preview.wo_match.reason}
                                    </p>
                                </div>
                            )}

                            {unmatched.length > 0 && (
                                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <p>
                                        <span className="font-medium">
                                            {unmatched.length} line{unmatched.length === 1 ? "" : "s"} didn't match a
                                            device — pick or create.
                                        </span>{" "}
                                        Pick the device below, or create the job now and add{" "}
                                        {unmatched.length === 1 ? "it" : "them"} on the job's Devices. Nothing else at
                                        the site is linked.
                                    </p>
                                </div>
                            )}

                            {preview.status === "no_serials" && (
                                <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                    <p>
                                        No device serials found on this estimate's lines. The job will create
                                        device-less with an "Add the quoted devices" reminder on its Results card.
                                    </p>
                                </div>
                            )}

                            <div className="divide-y rounded-lg border">
                                {preview.lines
                                    .filter((l) => l.description)
                                    .map((l) => (
                                        <div key={l.lineId} className="space-y-1.5 p-3">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className="text-sm">{l.description}</p>
                                                {l.status === "matched" && (
                                                    <Badge className="shrink-0 bg-green-700 text-white text-[10px]">
                                                        {l.evidence}
                                                    </Badge>
                                                )}
                                                {l.status === "ambiguous" && (
                                                    <Badge
                                                        variant="outline"
                                                        className="shrink-0 border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]"
                                                    >
                                                        needs review
                                                    </Badge>
                                                )}
                                                {l.status === "unmatched" && (
                                                    <Badge
                                                        variant="outline"
                                                        className="shrink-0 border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]"
                                                    >
                                                        no match at site
                                                    </Badge>
                                                )}
                                                {l.status === "declined" && (
                                                    <Badge
                                                        variant="outline"
                                                        className="shrink-0 text-[10px] text-foreground/60 line-through"
                                                    >
                                                        declined
                                                    </Badge>
                                                )}
                                                {l.status === "no_serial" && (
                                                    <Badge
                                                        variant="outline"
                                                        className="shrink-0 text-[10px] text-foreground/60"
                                                    >
                                                        no device
                                                    </Badge>
                                                )}
                                            </div>
                                            {l.status === "matched" && l.matchedAsset && (
                                                <p className="flex items-center gap-1.5 text-xs text-foreground/80">
                                                    <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                                                    {deviceLabel(l.matchedAsset)}
                                                </p>
                                            )}
                                            {(l.status === "ambiguous" || l.status === "unmatched") && (
                                                <div className="space-y-1.5">
                                                    <p className="text-xs text-amber-700 dark:text-amber-300">
                                                        {l.reviewReason}
                                                    </p>
                                                    <select
                                                        className="w-full rounded-lg border bg-background px-2 py-1.5 text-sm"
                                                        value={picks[l.lineId] ?? "__unset__"}
                                                        onChange={(e) =>
                                                            setPicks((p) => ({
                                                                ...p,
                                                                [l.lineId]:
                                                                    e.target.value === "__unset__"
                                                                        ? undefined!
                                                                        : e.target.value,
                                                            }))
                                                        }
                                                    >
                                                        <option value="__unset__">
                                                            {l.status === "unmatched"
                                                                ? "— Leave for later (add it on the job) —"
                                                                : "— Pick the device this line covers —"}
                                                        </option>
                                                        {l.candidates.map((c) => (
                                                            <option key={c.id} value={c.id}>
                                                                {deviceLabel(c)}
                                                            </option>
                                                        ))}
                                                        {l.status === "ambiguous" && (
                                                            <option value="">Skip — no device for this line</option>
                                                        )}
                                                    </select>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center justify-end gap-2 border-t px-4 py-3">
                    <Button type="button" size="sm" variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        type="button"
                        size="sm"
                        disabled={!preview || unresolved.length > 0 || convertM.isPending}
                        title={
                            unresolved.length > 0
                                ? `Resolve ${unresolved.length} line${unresolved.length === 1 ? "" : "s"} first`
                                : undefined
                        }
                        onClick={() => convertM.mutate()}
                    >
                        {convertM.isPending
                            ? "Creating…"
                            : unmatchedLeft > 0
                              ? `Create job — ${unmatchedLeft} line${unmatchedLeft === 1 ? "" : "s"} left to add`
                              : "Confirm & create job"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
